import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAppStore } from '@/store';
import * as api from '@/api/client';
import {
  RefreshCw, Lock, Unlock, CheckCircle,
  Loader2, ArrowRight, AlertTriangle, Check, SkipForward, Pencil, X,
} from 'lucide-react';
import StageGateBanner from '@/components/layout/StageGateBanner';
import StageAcknowledgment from '@/components/workspace/StageAcknowledgment';

type StrategyTab = 'novelty' | 'inventive' | 'strategy';

const _CLAIM_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'method',    label: 'Method' },
  { id: 'system',    label: 'System' },
  { id: 'crm',       label: 'CRM' },
  { id: 'apparatus', label: 'Apparatus' },
];

export default function Stage2() {
  const { matterState, setMatterState, activeStage, setActiveStage } = useAppStore();
  const matter = matterState?.matter;
  const matterId = matter?.id || '';
  const s2 = matterState?.stages?.stage_2;
  const content = s2?.content || {};
  const isFrozen = s2?.is_frozen;

  // Strategy is the Stage 2.5 content stored under stage_2_5 — we read
  // it here (via matterState.stages.stage_2_5) and render inside the
  // Strategy tab. Backend stage-id is unchanged; only the nav moves.
  const s25 = matterState?.stages?.stage_2_5;
  const s25Content = s25?.content || {};
  const strategyFrozen = Boolean(s25?.is_frozen);
  const strategyApproved = strategyFrozen && !!(s25Content.strategy_text || s25Content.strategy_skipped);

  // epi compliance Item 2 — review acknowledgment for stage_2 AND
  // stage_2_5. The stage_2 ack gates the "Freeze Both & Continue"
  // button on the main novelty / inventive-step tabs; the
  // stage_2_5 ack gates the Strategy tab's freeze-approve action.
  // Both persist via the shared /stage_state/{stage}/acknowledge
  // endpoint. Both default to whatever's in the stage content
  // blob so a return visit restores the previous check state.
  const persistedAckS2: string | null = (content as any)?.acknowledged_at || null;
  const [ackAtS2, setAckAtS2] = useState<string | null>(persistedAckS2);
  useEffect(() => { setAckAtS2(persistedAckS2); }, [persistedAckS2]);
  const persistedAckS25: string | null = (s25Content as any)?.acknowledged_at || null;
  const [ackAtS25, setAckAtS25] = useState<string | null>(persistedAckS25);
  useEffect(() => { setAckAtS25(persistedAckS25); }, [persistedAckS25]);
  const [ackError, setAckError] = useState<string | null>(null);

  const [noveltyText, setNoveltyText] = useState(content.novelty_text || '');
  const [inventiveText, setInventiveText] = useState(content.inventive_step_text || '');
  // Per-field manual-edit mode — set when the drafter clicks
  // [Unfreeze] on a frozen field. Switches the field's render to
  // the editable + amber-notice + Save & Re-freeze layout. Cleared
  // on Save & Re-freeze or Cancel. Tracked alongside a snapshot of
  // the original (last-frozen) text so Cancel can revert without a
  // round-trip.
  const [manualEditNovelty, setManualEditNovelty] = useState(false);
  const [manualEditInventive, setManualEditInventive] = useState(false);
  const [originalNovelty, setOriginalNovelty] = useState<string>(content.novelty_text || '');
  const [originalInventive, setOriginalInventive] = useState<string>(content.inventive_step_text || '');
  const [busyNovelty, setBusyNovelty] = useState(false);
  const [busyInventive, setBusyInventive] = useState(false);
  const [strategyText, setStrategyText] = useState<string>(s25Content.strategy_text || '');
  const [generating, setGenerating] = useState(false);
  // Separate timeout-state flag so the inline "Try again / Check
  // if saved" affordance can render without stomping the generic
  // `error` toast used for non-transient failures.
  const [genTimeout, setGenTimeout] = useState<string | null>(null);
  const [checkingSaved, setCheckingSaved] = useState(false);
  const [recoveredNotice, setRecoveredNotice] = useState<string | null>(null);
  const [generatingStrategy, setGeneratingStrategy] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [freezingStage, setFreezingStage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drafter must confirm Claim 1 category before Stage 3 can run
  // (prompt 04 backend gate). Stage 2 mirrors that constraint at
  // the top-bar Continue button and at the freeze-bar continue
  // button so the drafter can't reach Stage 3 with the wrong UX
  // (red banner saying "Claim generation failed"). Source of
  // truth is the backend; the Strategy tab's CategorySelectorCard
  // POSTs to /category/confirm and we re-poll here on
  // tab/freeze changes.
  const [categoryLockedAtParent, setCategoryLockedAtParent] = useState(false);
  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    (async () => {
      try {
        const state = await api.getClaimCategory(matterId);
        if (!cancelled) {
          setCategoryLockedAtParent(!!state.selected_category);
        }
      } catch { /* tolerate read failure — gate stays closed */ }
    })();
    return () => { cancelled = true; };
  }, [matterId, freezingStage]);

  // When the user navigates with activeStage='stage_2_5' (after Stage 2
  // freeze), land on the Strategy tab instead of a separate screen.
  const [tab, setTab] = useState<StrategyTab>(() =>
    activeStage === 'stage_2_5' ? 'strategy' : 'novelty',
  );
  useEffect(() => {
    if (activeStage === 'stage_2_5') setTab('strategy');
  }, [activeStage]);

  // Claim-categories selection is UI-only — persisted per matter in
  // localStorage so we don't need a backend change for this task. The
  // actual claim generator uses whatever categories Stage 3 drives
  // independently; this is the drafter's stated preference.
  const catsKey = `stage2.claim_categories.${matterId}`;
  const [claimCategories, setClaimCategories] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(catsKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(x => typeof x === 'string');
      }
    } catch { /* ignore */ }
    return ['method', 'system'];
  });
  useEffect(() => {
    try { localStorage.setItem(catsKey, JSON.stringify(claimCategories)); } catch {}
  }, [catsKey, claimCategories]);

  // Edit-inputs flow: when Strategy content already exists, we default
  // to a read-only preview. Clicking [Edit inputs] flips to editable.
  const [editStrategyInputs, setEditStrategyInputs] = useState(false);
  const [cancelSnapshot, setCancelSnapshot] = useState<{
    text: string; cats: string[];
  } | null>(null);

  useEffect(() => {
    setNoveltyText(content.novelty_text || '');
    setInventiveText(content.inventive_step_text || '');
  }, [content.novelty_text, content.inventive_step_text]);
  useEffect(() => {
    setStrategyText(s25Content.strategy_text || '');
  }, [s25Content.strategy_text]);

  const refreshMatter = useCallback(async () => {
    const data = await api.getMatter(matterId);
    setMatterState(data);
  }, [matterId, setMatterState]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setGenTimeout(null);
    setRecoveredNotice(null);
    try {
      // Resilient wrapper catches Cloudflare 524 / 502 / 504 /
      // network errors and re-fetches the matter to see if the
      // origin finished writing despite the edge timeout. Backend
      // already runs novelty + IS in parallel (server.py:7774 —
      // asyncio.gather), so the only remaining 524 vector is a
      // single LLM call exceeding 100 s; for that case the write
      // usually succeeds shortly after CF gave up and checkSaved
      // recovers the result.
      const result = await api.generateStage2Resilient(matterId);
      setNoveltyText(result.novelty_text || '');
      setInventiveText(result.inventive_step_text || '');
      if (result.recovered_from_timeout) {
        setRecoveredNotice(
          'Generation completed — displaying saved result. The '
          + 'network response was slow but the server finished.',
        );
      }
      await refreshMatter();
    } catch (e: any) {
      if (e?.name === 'GenerationTimeoutError') {
        // Render the inline timeout panel instead of the generic
        // red banner. The wrapper already tried once + checked
        // for a saved result; the user can retry manually or
        // poll "Check if saved" themselves.
        setGenTimeout(e.message || 'Generation timed out.');
      } else {
        setError(e.response?.data?.detail || 'Generation failed. Check LLM settings.');
      }
    }
    setGenerating(false);
  };

  const handleCheckIfSaved = async () => {
    setCheckingSaved(true);
    try {
      const saved = await api.checkStage2Saved(matterId);
      if (saved && (saved.novelty_text || saved.inventive_step_text)) {
        setNoveltyText(saved.novelty_text || '');
        setInventiveText(saved.inventive_step_text || '');
        setGenTimeout(null);
        setRecoveredNotice(
          'Generation completed — displaying saved result.',
        );
        await refreshMatter();
      } else {
        // Still nothing in the DB. Keep the timeout panel visible
        // so the user can retry.
      }
    } finally {
      setCheckingSaved(false);
    }
  };

  const handleSaveField = async (field: 'novelty' | 'inventive_step', text: string) => {
    try {
      await api.updateStage2Field(matterId, field, text);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    }
  };

  // ── Per-field manual-edit lifecycle ──────────────────────────
  // Unfreeze → flips the field to manual_edit mode without going
  // through the toggle endpoint; that gives the UI a clean
  // unidirectional contract (the toggle endpoint stays in place
  // for the legacy Approve / Approved button cycle).
  const handleUnfreezeField = async (field: 'novelty' | 'inventive_step') => {
    const setBusy = field === 'novelty' ? setBusyNovelty : setBusyInventive;
    const setManual = field === 'novelty' ? setManualEditNovelty : setManualEditInventive;
    const setOrig = field === 'novelty' ? setOriginalNovelty : setOriginalInventive;
    const currentText = field === 'novelty' ? noveltyText : inventiveText;
    setBusy(true);
    try {
      await api.unfreezeStage2Field(matterId, field);
      // Snapshot the last-frozen text so Cancel can revert. Use
      // the live noveltyText/inventiveText state which mirrors the
      // server's frozen value at this point.
      setOrig(currentText);
      setManual(true);
      await refreshMatter();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not unfreeze field.');
    } finally {
      setBusy(false);
    }
  };

  // Save & Re-freeze → PATCH the edited text and re-freeze. No QC
  // re-run, no Stage 3 invalidation; the amber notice in the UI is
  // the explicit signal that downstream stages won't auto-update.
  const handleSaveRefreezeField = async (field: 'novelty' | 'inventive_step') => {
    const setBusy = field === 'novelty' ? setBusyNovelty : setBusyInventive;
    const setManual = field === 'novelty' ? setManualEditNovelty : setManualEditInventive;
    const text = field === 'novelty' ? noveltyText : inventiveText;
    if (!text.trim()) {
      setError(`Cannot save empty ${field === 'novelty' ? 'novelty' : 'inventive step'} text.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateStage2Field(matterId, field, text);
      // freeze_field is a toggle; field is currently unfrozen
      // (manual edit mode), so this call freezes it.
      await api.freezeStage2Field(matterId, field);
      setManual(false);
      await refreshMatter();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Save & Re-freeze failed.');
    } finally {
      setBusy(false);
    }
  };

  // Cancel manual edit — revert text to the last-frozen snapshot
  // and re-freeze. No PATCH; if the original text was already on
  // the server (which it must be, since it was previously frozen),
  // calling freeze_field re-applies the frozen flag using the
  // existing server-side text.
  const handleCancelEdit = async (field: 'novelty' | 'inventive_step') => {
    const setBusy = field === 'novelty' ? setBusyNovelty : setBusyInventive;
    const setManual = field === 'novelty' ? setManualEditNovelty : setManualEditInventive;
    const setText = field === 'novelty' ? setNoveltyText : setInventiveText;
    const orig = field === 'novelty' ? originalNovelty : originalInventive;
    setBusy(true);
    try {
      setText(orig);
      // Push the original text back so the freeze_field call
      // doesn't pick up any half-typed in-flight content. Best-
      // effort — if PATCH fails we still re-freeze the existing
      // server-side text.
      try { await api.updateStage2Field(matterId, field, orig); } catch { /* tolerate */ }
      await api.freezeStage2Field(matterId, field);
      setManual(false);
      await refreshMatter();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Cancel failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleFieldFreeze = async (field: 'novelty' | 'inventive_step') => {
    try {
      // Persist any unsaved text the drafter typed before clicking
      // Approve — otherwise the server sees empty text and refuses.
      const text = field === 'novelty' ? noveltyText : inventiveText;
      if (text) {
        try { await api.updateStage2Field(matterId, field, text); } catch { /* non-fatal */ }
      }
      await api.freezeStage2Field(matterId, field);
      await refreshMatter();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === 'string' && detail
          ? detail
          : `Could not ${field === 'novelty' ? 'approve novelty' : 'approve inventive step'}. Make sure the statement has content.`,
      );
    }
  };

  const handleFreezeStage = async () => {
    setFreezingStage(true);
    setError(null);
    try {
      // Auto-approve Novelty and Inventive Step as part of the freeze
      // action. Previously the drafter had to click "Approve" on each
      // tab and then click "Freeze Both & Continue" — now one button
      // handles both. Empty-text guard: still fail loudly if the text
      // is blank (matching the per-field freeze semantics).
      if (!content.novelty_frozen) {
        if (!noveltyText.trim()) {
          throw new Error('Novelty statement cannot be empty.');
        }
        await api.updateStage2Field(matterId, 'novelty', noveltyText);
        await api.freezeStage2Field(matterId, 'novelty');
      }
      if (!content.inventive_step_frozen) {
        if (!inventiveText.trim()) {
          throw new Error('Inventive Step statement cannot be empty.');
        }
        await api.updateStage2Field(matterId, 'inventive_step', inventiveText);
        await api.freezeStage2Field(matterId, 'inventive_step');
      }
      await api.freezeStage2(matterId);
      await refreshMatter();
      // Auto-advance to Stage 3. The post-freeze banner used to sit
      // visible with a "Continue to Stage 3" button that needed a
      // second click; Freeze Both & Continue now lives up to its
      // name and flips activeStage directly.
      setActiveStage('stage_3');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      setError(
        typeof detail === 'string' && detail
          ? detail
          : 'Could not freeze. Check that Novelty and Inventive Step both have content.',
      );
    }
    setFreezingStage(false);
  };

  const handleSaveStrategyText = async (text: string) => {
    try {
      await api.updateStrategy(matterId, text);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleSkipStrategy = async () => {
    setSkipping(true);
    setError(null);
    try {
      await api.skipStrategy(matterId);
      await refreshMatter();
      setEditStrategyInputs(false);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Skip failed');
    }
    setSkipping(false);
  };

  const handleGenerateStrategy = async () => {
    setGeneratingStrategy(true);
    setError(null);
    try {
      await api.updateStrategy(matterId, strategyText);
      await api.confirmStrategy(matterId);
      await refreshMatter();
      setEditStrategyInputs(false);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Strategy save failed');
    }
    setGeneratingStrategy(false);
  };

  const beginEditStrategy = () => {
    setCancelSnapshot({ text: strategyText, cats: [...claimCategories] });
    setEditStrategyInputs(true);
  };

  const cancelEditStrategy = () => {
    if (cancelSnapshot) {
      setStrategyText(cancelSnapshot.text);
      setClaimCategories(cancelSnapshot.cats);
    }
    setCancelSnapshot(null);
    setEditStrategyInputs(false);
  };

  const toggleCategory = (id: string) => {
    setClaimCategories((prev) =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  const hasContent = !!noveltyText && !!inventiveText;
  const needsGeneration = !content.novelty_text && !content.inventive_step_text;

  // Stage status counter: 3 tabs can be "approved" (frozen for novelty /
  // inventive, confirmed-or-skipped for strategy). Strategy is optional;
  // 2/3 is enough to continue, 3/3 is complete.
  const noveltyApproved = Boolean(content.novelty_frozen);
  const inventiveApproved = Boolean(content.inventive_step_frozen);
  const approvedCount = Number(noveltyApproved) + Number(inventiveApproved) + Number(strategyApproved);

  const tabs: Array<{ id: StrategyTab; label: string; approved: boolean; optional?: boolean }> = [
    { id: 'novelty',   label: 'Novelty',        approved: noveltyApproved },
    { id: 'inventive', label: 'Inventive Step', approved: inventiveApproved },
    { id: 'strategy',  label: 'Strategy',       approved: strategyApproved, optional: true },
  ];

  // ── Header (compact: title + status on one line) ───────────────
  return (
    <div className="max-w-4xl mx-auto pt-16 pb-12 px-6" data-testid="stage-2-container">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1
          data-testid="stage-2-title"
          className="text-2xl font-semibold tracking-tight text-zinc-900"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Stage 2 — Novelty & Inventive Step
        </h1>
        <StageStatusPill approved={approvedCount} total={3} />
      </div>
      <p
        className="text-xs text-zinc-500 mb-4 truncate"
        data-testid="stage-2-subtitle"
        title="Draft and freeze the novelty and inventive step statements, then (optionally) note claim strategy"
      >
        Draft and freeze the novelty and inventive step statements, then (optionally) note claim strategy
      </p>

      {error && (
        <div data-testid="stage-2-error" className="mb-3 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-center gap-2">
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Resilient-generation success notice — shown when the
          wrapper recovered a saved result after a Cloudflare 524
          / network hiccup. Green so it reads as "this worked",
          not as a warning. Auto-dismissed only when the user
          kicks off another action (handleGenerate clears it). */}
      {recoveredNotice && (
        <div
          data-testid="stage-2-recovered-notice"
          className="mb-3 p-2 bg-green-50 border border-green-200 rounded-sm text-xs text-green-800 flex items-start gap-2"
        >
          <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{recoveredNotice}</span>
        </div>
      )}

      {/* Inline generation-timeout panel — replaces the generic
          red banner for 524 / 502 / 504 / network errors where
          the write may still have succeeded. Two actions:
            "Try again" re-runs handleGenerate (which itself
                retries once + checks-if-saved internally).
            "Check if saved" just re-fetches the matter and
                promotes the saved result if present, without
                running the LLM again. Useful when the drafter
                suspects the generation finished but Cloudflare
                dropped the edge connection. */}
      {genTimeout && (
        <div
          data-testid="stage-2-timeout-panel"
          className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900"
        >
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle size={14} className="flex-shrink-0" />
            Generation timed out
          </div>
          <p className="mt-1.5 leading-relaxed">
            The server is taking longer than expected. This sometimes
            happens with large documents. The result may have been
            saved even though the response didn't come back.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              data-testid="stage-2-timeout-retry"
              onClick={handleGenerate}
              disabled={generating || checkingSaved}
              className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1 text-[11px] font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Try again
            </button>
            <button
              type="button"
              data-testid="stage-2-timeout-check-saved"
              onClick={handleCheckIfSaved}
              disabled={generating || checkingSaved}
              className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1 text-[11px] font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkingSaved ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle size={11} />}
              Check if saved
            </button>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div
        data-testid="stage-2-tabs"
        className="flex items-center gap-1 border-b border-zinc-200 mb-4"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`stage-2-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={
              'relative px-3 py-1.5 -mb-px text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ' +
              (tab === t.id
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-zinc-600 hover:text-zinc-900')
            }
          >
            {t.label}
            {t.optional && (
              <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-400">
                optional
              </span>
            )}
            {t.approved && (
              <span
                className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full bg-green-100 text-green-700 ml-1"
                data-testid={`stage-2-tab-${t.id}-approved`}
              >
                <Check size={9} />
              </span>
            )}
          </button>
        ))}
        <div className="ml-auto pr-1">
          {!isFrozen && hasContent && tab !== 'strategy' && (
            <button
              data-testid="stage-2-regenerate-all"
              onClick={handleGenerate}
              disabled={generating}
              className="text-[11px] text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 px-2 py-1 disabled:opacity-50"
            >
              {generating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Regenerate all
            </button>
          )}
        </div>
      </div>

      {needsGeneration && !isFrozen && tab !== 'strategy' && (
        <button
          data-testid="generate-stage-2-button"
          onClick={handleGenerate}
          disabled={generating}
          className="mb-4 bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {generating ? 'Generating…' : 'Generate Novelty & Inventive Step'}
        </button>
      )}

      {/* Tab panels */}
      {tab === 'novelty' && (() => {
        // Mode resolution priority:
        //   1. manualEditNovelty wins — the drafter explicitly
        //      flipped this field into manual-edit and we should
        //      stay there until Save or Cancel
        //   2. content.novelty_frozen → 'frozen' (read-only)
        //   3. otherwise → 'editable' (post-Generate, pre-Approve)
        // isFrozen (stage-level) blocks every interaction —
        // disabled buttons, no manual-edit transitions.
        const mode: StatementCardMode = manualEditNovelty
          ? 'manual_edit'
          : content.novelty_frozen
            ? 'frozen'
            : 'editable';
        return (
          <StatementCard
            testId="novelty-section"
            title="Novelty Statement"
            value={noveltyText}
            onChange={setNoveltyText}
            onBlurSave={(t) => !content.novelty_frozen && !manualEditNovelty
              ? handleSaveField('novelty', t)
              : undefined}
            mode={mode}
            busy={busyNovelty}
            onApprove={!isFrozen && noveltyText
              ? () => handleToggleFieldFreeze('novelty')
              : undefined}
            onUnfreeze={!isFrozen ? () => handleUnfreezeField('novelty') : undefined}
            onSaveRefreeze={() => handleSaveRefreezeField('novelty')}
            onCancelEdit={() => handleCancelEdit('novelty')}
            placeholder="Novelty statement will appear here after generation…"
            textareaTestId="novelty-textarea"
            freezeTestId="freeze-novelty-button"
          />
        );
      })()}

      {tab === 'inventive' && (() => {
        const mode: StatementCardMode = manualEditInventive
          ? 'manual_edit'
          : content.inventive_step_frozen
            ? 'frozen'
            : 'editable';
        return (
          <StatementCard
            testId="inventive-step-section"
            title="Inventive Step Statement"
            value={inventiveText}
            onChange={setInventiveText}
            onBlurSave={(t) => !content.inventive_step_frozen && !manualEditInventive
              ? handleSaveField('inventive_step', t)
              : undefined}
            mode={mode}
            busy={busyInventive}
            onApprove={!isFrozen && inventiveText
              ? () => handleToggleFieldFreeze('inventive_step')
              : undefined}
            onUnfreeze={!isFrozen ? () => handleUnfreezeField('inventive_step') : undefined}
            onSaveRefreeze={() => handleSaveRefreezeField('inventive_step')}
            onCancelEdit={() => handleCancelEdit('inventive_step')}
            placeholder="Inventive step statement will appear here after generation…"
            textareaTestId="inventive-step-textarea"
            freezeTestId="freeze-inventive-button"
          />
        );
      })()}

      {tab === 'strategy' && (
        <StrategyCardsTabPanel
          matterId={matterId}
          content={s25Content}
          locked={Boolean((s25 as any)?.claim_strategy_frozen) || strategyFrozen}
          claimCategories={claimCategories}
          toggleCategory={toggleCategory}
          refreshMatter={refreshMatter}
          onError={setError}
          onCategoryLockedChange={setCategoryLockedAtParent}
        />
      )}

      {/* Sticky freeze bar — visible on every tab including Strategy
          so the drafter always has a clear path to Stage 3 regardless
          of which tab they're on. Freeze gate honours the spec:
          Novelty + Inventive Step are required, Strategy is optional.
          Error surfacing uses the handler's existing setError path so
          the server-side reason shows instead of a generic banner. */}
      {!isFrozen && (() => {
        // Gate is now text-content-based, not pre-approval-based. The
        // per-tab [Approve] button still exists (lets the drafter lock
        // one tab while editing the other), but it is no longer a
        // prerequisite for the freeze — clicking Freeze Both auto-
        // approves any unapproved tab that has text. This matches what
        // the button label implies.
        const hasNovelty = !!noveltyText.trim() || noveltyApproved;
        const hasInventive = !!inventiveText.trim() || inventiveApproved;
        const canFreeze = hasNovelty && hasInventive;
        const onStrategyLocked = tab === 'strategy' && strategyApproved;
        // Pick the most specific blocking message so the drafter sees
        // which field is missing rather than a generic "fill in both".
        let gateMessage: string | null = null;
        if (!hasNovelty && !hasInventive) {
          gateMessage = 'Generate your Novelty and Inventive Step statements in Stage 2 to continue.';
        } else if (!hasNovelty) {
          gateMessage = 'Generate your Novelty statement in Stage 2 to continue.';
        } else if (!hasInventive) {
          gateMessage = 'Generate your Inventive Step statement in Stage 2 to continue.';
        }
        return (
          <>
          {gateMessage && (
            <StageGateBanner
              testId="stage-2-gate-banner"
              status="blocked"
              message={gateMessage}
              className="mx-6 mt-4"
            />
          )}
          <div
            data-testid="stage-2-freeze-bar"
            className="sticky bottom-0 -mx-6 mt-6 border-t border-zinc-200 bg-white/95 backdrop-blur px-6 py-3 flex items-center justify-between gap-3"
            style={{ boxShadow: '0 -2px 6px rgba(0,0,0,0.04)' }}
          >
            <div className="text-[11px] text-zinc-500">
              {onStrategyLocked && canFreeze && (
                <span className="text-green-700">
                  Strategy locked ✓ — click <span className="font-medium">Freeze Both & Continue</span> to proceed to Stage 3.
                </span>
              )}
              {!canFreeze && (
                <span data-testid="stage-2-freeze-gate-hint">
                  Fill in both Novelty and Inventive Step first
                </span>
              )}
              {canFreeze && !onStrategyLocked && (
                <span className="text-zinc-600">
                  Ready to freeze — strategy is optional.
                </span>
              )}
            </div>
            {/* epi compliance Item 2 — review acknowledgment above
                the Freeze button. AND-ed with canFreeze in the
                disabled logic below. */}
            <StageAcknowledgment
              matterId={matterId}
              stage="stage_2"
              initialAcknowledgedAt={ackAtS2}
              onChange={setAckAtS2}
              disabled={!!isFrozen}
              onError={setAckError}
            />
            <button
              data-testid="freeze-stage-2-button"
              onClick={handleFreezeStage}
              disabled={!canFreeze || !ackAtS2 || freezingStage}
              title={
                !canFreeze
                  ? 'Fill in both Novelty and Inventive Step first'
                  : !ackAtS2
                    ? 'Check the review-acknowledged box to continue'
                    : 'Freeze Stage 2 and advance to Stage 3'
              }
              className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                canFreeze && ackAtS2
                  ? 'bg-[#002FA7] text-white hover:bg-[#002277]'
                  : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
              }`}
            >
              {freezingStage ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              Freeze Both & Continue
            </button>
          </div>
          </>
        );
      })()}

      {isFrozen && (
        <div
          data-testid="stage-2-frozen"
          className="sticky bottom-0 -mx-6 mt-6 px-6 py-3 bg-green-50 border-t border-green-200 flex items-center justify-between gap-3 text-sm text-green-800"
        >
          <span className="flex items-center gap-2 min-w-0">
            <CheckCircle size={16} className="flex-shrink-0" />
            Stage 2 frozen — review Strategy tab (optional) or proceed to Stage 3
          </span>
          {/* Explicit forward nav after freeze. Previously the banner
              said 'proceed to Stage 3' but offered no affordance — the
              drafter had to use the top nav. Now a dedicated button
              flips activeStage so the flow stays inside this one
              panel. */}
          {!categoryLockedAtParent && (
            <span
              data-testid="stage-2-category-required-notice"
              className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 flex-shrink-0"
              title="Stage 3 cannot run until Claim 1 category is locked"
            >
              <AlertTriangle size={11} />
              Confirm category in Strategy tab first
            </span>
          )}
          <button
            type="button"
            data-testid="stage-2-continue-to-stage-3"
            onClick={() => {
              if (!categoryLockedAtParent) {
                // Send the drafter to the Strategy tab where the
                // category card lives instead of letting them land
                // on the Stage 3 amber banner.
                setTab('strategy');
                return;
              }
              setActiveStage('stage_3');
            }}
            disabled={!categoryLockedAtParent}
            className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50 disabled:cursor-not-allowed rounded-sm px-3 py-1.5 text-xs font-medium transition-colors flex-shrink-0"
          >
            Continue to Stage 3 <ArrowRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}


function StageStatusPill({ approved, total }: { approved: number; total: number }) {
  const full = approved >= total;
  return (
    <span
      data-testid="stage-2-status"
      className={
        'inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border shrink-0 ' +
        (full
          ? 'bg-green-50 text-green-800 border-green-200'
          : 'bg-zinc-50 text-zinc-600 border-zinc-200')
      }
    >
      Stage status: {approved} of {total} approved
    </span>
  );
}


/**
 * Three rendering modes for a per-field freeze surface:
 *   'frozen'      — read-only text, [Unfreeze] button
 *   'manual_edit' — editable, amber notice, [Save & Re-freeze] + [Cancel]
 *   'editable'    — pristine editable (post-Generate, never approved
 *                    OR stage-frozen-then-unfrozen via the toggle path)
 *                    Shows the original [Approve] button + auto-save
 *                    on blur.
 */
type StatementCardMode = 'frozen' | 'manual_edit' | 'editable';

function StatementCard({
  testId, title, value, onChange, onBlurSave, mode,
  onApprove, onUnfreeze, onSaveRefreeze, onCancelEdit,
  placeholder, textareaTestId, freezeTestId,
  busy = false,
}: {
  testId: string; title: string; value: string;
  onChange: (v: string) => void;
  onBlurSave: (v: string) => void;
  mode: StatementCardMode;
  /** Called in 'editable' mode — initial Approve + freeze. */
  onApprove?: () => void;
  /** Called in 'frozen' mode — flips to 'manual_edit'. */
  onUnfreeze?: () => void;
  /** Called in 'manual_edit' mode — PATCH text + re-freeze. */
  onSaveRefreeze?: () => void;
  /** Called in 'manual_edit' mode — revert text to last-frozen value
   *  and re-freeze without saving the in-flight edits. */
  onCancelEdit?: () => void;
  placeholder: string; textareaTestId: string; freezeTestId: string;
  /** True while any of the three transitions is in flight. Disables
   *  every button so a double-click can't fire two state changes. */
  busy?: boolean;
}) {
  const isFrozen = mode === 'frozen';
  const isManualEdit = mode === 'manual_edit';
  const isReadOnly = isFrozen;  // editable + manual_edit both allow typing
  return (
    <div className="border border-zinc-200 rounded-sm bg-white mb-4" data-testid={testId}>
      <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-zinc-100">
        <h2 className="text-sm font-medium text-zinc-900">{title}</h2>
        <div className="flex items-center gap-2">
          {isFrozen && onUnfreeze && (
            <button
              data-testid={`unfreeze-${freezeTestId}`}
              onClick={onUnfreeze}
              disabled={busy}
              title="Unfreeze for manual editing — downstream stages will not be re-run"
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium transition-colors bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 disabled:opacity-50"
            >
              <Unlock size={11} /> Unfreeze
            </button>
          )}
          {isFrozen && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-green-100 text-green-800 border border-green-200">
              <Lock size={11} /> Approved
            </span>
          )}
          {isManualEdit && onSaveRefreeze && (
            <button
              data-testid={`save-refreeze-${freezeTestId}`}
              onClick={onSaveRefreeze}
              disabled={busy || !value.trim()}
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium transition-colors bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Lock size={11} />}
              Save &amp; Re-freeze
            </button>
          )}
          {isManualEdit && onCancelEdit && (
            <button
              data-testid={`cancel-edit-${freezeTestId}`}
              onClick={onCancelEdit}
              disabled={busy}
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium transition-colors bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          {mode === 'editable' && onApprove && (
            <button
              data-testid={freezeTestId}
              onClick={onApprove}
              disabled={busy || !value.trim()}
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium transition-colors bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 disabled:opacity-50"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Unlock size={11} />}
              Approve
            </button>
          )}
        </div>
      </div>

      {isManualEdit && (
        <div
          data-testid={`manual-edit-notice-${freezeTestId}`}
          className="mx-5 mt-3 mb-1 p-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900 flex items-start gap-1.5"
        >
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-600" />
          <span>
            Manual edit. Downstream stages are not automatically updated —
            re-run QC after Stage 4 if needed.
          </span>
        </div>
      )}

      <div className="px-5 py-4">
        <textarea
          data-testid={textareaTestId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onBlurSave(value)}
          disabled={isReadOnly}
          rows={8}
          placeholder={placeholder}
          className="w-full bg-white border border-zinc-300 rounded-sm px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent disabled:bg-zinc-50 disabled:text-zinc-600 resize-y overflow-y-auto"
          style={{ fontFamily: 'IBM Plex Serif, serif', maxHeight: '320px' }}
        />
      </div>
    </div>
  );
}


/** Tailwind colour classes for the strategy-card priority chip.
 *  Hoisted to module scope so the locked-card component (sibling
 *  of StrategyCardsTabPanel) can apply the same colour mapping
 *  without duplicating the literal. */
const priorityClass = (p: string) => (
  p === 'high'   ? 'bg-red-100 text-red-700 border-red-200' :
  p === 'low'    ? 'bg-green-100 text-green-700 border-green-200' :
                   'bg-amber-100 text-amber-800 border-amber-200'
);


function StrategyCardsTabPanel({
  matterId, content, locked, claimCategories, toggleCategory,
  refreshMatter, onError, onCategoryLockedChange,
}: {
  matterId: string;
  content: any;
  locked: boolean;
  claimCategories: string[];
  toggleCategory: (id: string) => void;
  refreshMatter: () => Promise<void>;
  onError: (msg: string | null) => void;
  /** Notifies the parent component (Stage2) when the Claim 1
   *  category lock state changes, so the parent's "Continue to
   *  Stage 3" gate updates without a full refresh. */
  onCategoryLockedChange?: (locked: boolean) => void;
}) {
  // Cards persist in stage_2_5.content.cards (populated by the
  // generate endpoint) — seed local state from there so a refresh
  // preserves the drafter's work-in-progress set.
  const seedCards: api.StrategyCard[] = React.useMemo(
    () => (Array.isArray(content.cards) ? content.cards : []) as api.StrategyCard[],
    [content.cards],
  );
  const [cards, setCards] = React.useState<api.StrategyCard[]>(seedCards);
  React.useEffect(() => { setCards(seedCards); }, [seedCards]);

  // ── Claim 1 category selector ─────────────────────────────────
  // Stage 3 generate_claims is gated on selected_category being
  // locked. Drafters land in the Strategy tab from Stage 2 freeze,
  // and the category card here is the canonical place to confirm.
  // Without this, the only path to confirm was the dead-code
  // Stage25.tsx page that no router reaches.
  const [catState, setCatState] = React.useState<
    api.ClaimCategoryState | null
  >(null);
  const [pickedCategory, setPickedCategory] = React.useState<
    api.ClaimCategory | null
  >(null);
  const [pickedSecondaries, setPickedSecondaries] = React.useState<
    Set<api.ClaimCategory>
  >(new Set());
  const [suggestingCat, setSuggestingCat] = React.useState(false);
  const [confirmingCat, setConfirmingCat] = React.useState(false);
  const [catError, setCatError] = React.useState<string | null>(null);

  const toggleSecondary = (cat: api.ClaimCategory) => {
    setPickedSecondaries(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  React.useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    (async () => {
      try {
        const state = await api.getClaimCategory(matterId);
        if (cancelled) return;
        setCatState(state);
        // Seed secondaries from persisted state so the drafter
        // sees their previous picks on refresh.
        const sec = state.secondary_categories || [];
        if (sec.length) setPickedSecondaries(new Set(sec));
        if (state.selected_category) {
          setPickedCategory(state.selected_category);
        } else if (state.suggested_category) {
          setPickedCategory(state.suggested_category);
        } else {
          setSuggestingCat(true);
          try {
            const fresh = await api.suggestClaimCategory(matterId);
            if (cancelled) return;
            setCatState(fresh);
            if (fresh.suggested_category) {
              setPickedCategory(fresh.suggested_category);
            }
          } catch (e: any) {
            if (!cancelled) {
              setCatError(
                e?.response?.data?.detail
                  || 'Could not run category classifier — pick manually.',
              );
            }
          } finally {
            if (!cancelled) setSuggestingCat(false);
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setCatError(
            e?.response?.data?.detail
              || 'Could not load category state',
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [matterId]);

  const handleConfirmCategory = async () => {
    if (!pickedCategory) return;
    setConfirmingCat(true);
    setCatError(null);
    // Strip the primary out of the secondaries set defensively —
    // the backend rejects duplication, but we shouldn't even ask.
    const sec: api.ClaimCategory[] = Array.from(pickedSecondaries).filter(
      (c) => c !== pickedCategory,
    );
    try {
      const fresh = await api.confirmClaimCategory(
        matterId, pickedCategory, sec,
      );
      setCatState(fresh);
    } catch (e: any) {
      setCatError(
        e?.response?.data?.detail || 'Could not confirm category',
      );
    } finally {
      setConfirmingCat(false);
    }
  };

  const categoryLocked = !!catState?.selected_category;
  React.useEffect(() => {
    if (onCategoryLockedChange) onCategoryLockedChange(categoryLocked);
  }, [categoryLocked, onCategoryLockedChange]);

  const [generating, setGenerating] = React.useState(false);
  const [locking, setLocking] = React.useState(false);
  const [unlocking, setUnlocking] = React.useState(false);
  const [genError, setGenError] = React.useState<{ reason: string; raw?: string } | null>(null);
  const [discussOpenId, setDiscussOpenId] = React.useState<string | null>(null);

  // epi compliance Item 2 — Stage 2.5 review acknowledgment.
  // Drafters who DO enter the Strategy tab must ack before the
  // Lock Strategy button enables. Sourced from stage_2_5.content
  // the same way every other stage's ack is sourced; the skip
  // path (drafter never enters 2.5) stays unchanged — Stage 2's
  // ack carries them through.
  const persistedAck: string | null = (content as any)?.acknowledged_at || null;
  const [ackAt, setAckAt] = React.useState<string | null>(persistedAck);
  React.useEffect(() => { setAckAt(persistedAck); }, [persistedAck]);

  const selectedCount = cards.filter(c => c.selected !== false).length;

  const patchCard = (id: string, patch: Partial<api.StrategyCard>) => {
    setCards(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  };
  const removeCard = (id: string) => setCards(prev => prev.filter(c => c.id !== id));
  const addCustomCard = () => {
    const nextId = `custom_${Date.now()}`;
    setCards(prev => [
      ...prev,
      {
        id: nextId, title: 'New direction', direction: '',
        rationale: '', priority: 'medium', comment: '', selected: true,
      },
    ]);
  };

  const doGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    onError(null);
    try {
      const resp = await api.generateStrategyCards(matterId, {
        claim_categories: claimCategories,
      });
      if (resp.error) {
        setGenError({ reason: resp.reason || resp.error, raw: resp.raw });
      } else if (Array.isArray(resp.cards)) {
        setCards(resp.cards);
      }
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Failed to generate strategy cards');
    } finally {
      setGenerating(false);
    }
  };

  // Confirmation modal — fires when the drafter tries to Lock with
  // fewer than half the cards selected. Pre-fix the lock went
  // through silently and the model lost most of the strategy
  // signal without any nudge.
  const [pendingLowSelectionLock, setPendingLowSelectionLock] = React.useState(false);

  const performLock = async () => {
    setLocking(true);
    onError(null);
    try {
      await api.lockStrategyCards(matterId, cards);
      await refreshMatter();
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Failed to lock strategy');
    } finally {
      setLocking(false);
    }
  };

  const doLock = async () => {
    if (selectedCount === 0) return;
    // Soft gate: confirm before locking when the drafter has
    // de-selected more than half the cards. Threshold is strict
    // <50% selected (so 5/10 selected is fine, 4/10 prompts).
    const total = cards.length;
    if (total >= 2 && selectedCount * 2 < total) {
      setPendingLowSelectionLock(true);
      return;
    }
    await performLock();
  };

  const doUnlock = async () => {
    setUnlocking(true);
    onError(null);
    try {
      await api.unlockStrategyCards(matterId);
      await refreshMatter();
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Failed to unlock strategy');
    } finally {
      setUnlocking(false);
    }
  };

  // priorityClass is defined at module scope so StrategyCardLocked
  // (a sibling component) can reuse the same colour mapping.

  // ── Category-selector card (rendered at top of every Strategy
  //    render state). Stage 3 generate_claims is gated on a locked
  //    Claim 1 category; this card is the canonical place to
  //    confirm. The card stays visible (in locked form) after
  //    confirmation so the drafter knows what they picked.
  const CATEGORY_DESCRIPTIONS: Record<api.ClaimCategory, string> = {
    apparatus:   'physical hardware / device',
    system:      'multi-component architecture',
    method:      'sequence of steps (procedural invention)',
    composition: 'chemical / biological / material',
    use:         'second medical use, new use of compound',
    kit:         'combination of articles packaged together',
    crm:         'non-transitory computer-readable medium',
  };
  const CATEGORY_ORDER: api.ClaimCategory[] = [
    'apparatus', 'system', 'method',
    'composition', 'use', 'kit', 'crm',
  ];
  const categoryCard = (
    <div
      data-testid="claim-category-card"
      className="border border-zinc-200 rounded-sm bg-white"
    >
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            Claim 1 category
          </span>
          <span
            className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200"
            title="Stage 3 cannot run until this is confirmed"
          >
            Required
          </span>
        </div>
        {categoryLocked && (
          <span
            data-testid="claim-category-locked"
            className="inline-flex items-center gap-1 text-xs text-green-800"
          >
            <Lock size={12} /> Locked: {catState?.selected_category}
          </span>
        )}
      </div>
      <div className="px-4 py-4">
        <div className="text-sm text-zinc-700 mb-2 leading-snug">
          {suggestingCat ? (
            <span className="inline-flex items-center gap-2 text-zinc-500">
              <Loader2 size={14} className="animate-spin" />
              Classifier running… determining the recommended Claim 1 category from your IS analysis and IDF summary.
            </span>
          ) : catState?.suggested_category ? (
            <>
              <span className="text-zinc-500">Suggested:</span>{' '}
              <span className="font-semibold text-zinc-900">
                {catState.suggested_category}
              </span>{' '}
              <span className="text-zinc-500">
                — accept by leaving the row below selected, or override with another button.
              </span>
            </>
          ) : (
            <span className="text-zinc-500">
              Pick the primary category for Claim 1. Your choice locks Stage 3.
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
          {CATEGORY_ORDER.map((cat) => {
            const selected = pickedCategory === cat;
            const isSuggested = catState?.suggested_category === cat;
            return (
              <button
                key={cat}
                type="button"
                data-testid={`category-button-${cat}`}
                disabled={categoryLocked}
                onClick={() => setPickedCategory(cat)}
                className={
                  'text-left text-sm px-3 py-2 rounded-sm border transition-colors '
                  + (selected
                    ? 'border-[#002FA7] bg-[#002FA7]/5 text-zinc-900'
                    : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400')
                  + (categoryLocked ? ' opacity-60 cursor-not-allowed' : '')
                }
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium capitalize">{cat}</span>
                  {isSuggested && (
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1">
                      suggested
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 leading-tight">
                  {CATEGORY_DESCRIPTIONS[cat]}
                </div>
              </button>
            );
          })}
        </div>

        {/* Secondary independents — checkboxes for additional
            categories the drafter wants generated as independent
            claims beyond Claim 1. Hidden when category is locked
            (read-only display below shows the locked set). */}
        {!categoryLocked && (
          <div className="mb-3" data-testid="secondary-categories-row">
            <div className="text-[11px] uppercase tracking-wide font-medium text-zinc-500 mb-1">
              Also include as secondary independent claims
              <span className="normal-case tracking-normal text-zinc-400 font-normal">
                {' '}— optional
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_ORDER
                .filter((cat) => cat !== pickedCategory)
                .map((cat) => {
                  const checked = pickedSecondaries.has(cat);
                  return (
                    <label
                      key={cat}
                      data-testid={`secondary-toggle-${cat}`}
                      className={
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs cursor-pointer transition-colors '
                        + (checked
                          ? 'border-[#002FA7] bg-[#002FA7]/5 text-zinc-900'
                          : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSecondary(cat)}
                        className="accent-[#002FA7]"
                      />
                      <span className="font-medium capitalize">{cat}</span>
                    </label>
                  );
                })}
            </div>
            {pickedSecondaries.size > 0 && (
              <div className="text-[10px] text-zinc-500 mt-1">
                Stage 3 will produce one independent claim per checked
                category, after Claim 1 ({pickedCategory}). Total claim
                count still respects the per-jurisdiction cap.
              </div>
            )}
          </div>
        )}

        {/* Locked read-only display of the selected secondaries. */}
        {categoryLocked && (catState?.secondary_categories?.length ?? 0) > 0 && (
          <div
            data-testid="locked-secondaries"
            className="mb-3 text-[11px] text-zinc-600"
          >
            <span className="uppercase tracking-wide font-medium text-zinc-500">
              Plus secondary independents:{' '}
            </span>
            {(catState?.secondary_categories || []).map((c, i, arr) => (
              <span key={c} className="capitalize font-medium text-zinc-800">
                {c}{i < arr.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}

        {catError && (
          <div
            data-testid="category-error"
            className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2.5 py-1.5 mb-2 inline-flex items-start gap-1.5"
          >
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            {catError}
          </div>
        )}
        {!categoryLocked && (
          <button
            type="button"
            data-testid="confirm-category-button"
            disabled={!pickedCategory || confirmingCat}
            onClick={handleConfirmCategory}
            className="text-sm px-3 py-1.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#002277] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            {confirmingCat
              ? <Loader2 size={14} className="animate-spin" />
              : <Lock size={14} />}
            Confirm category
            {pickedSecondaries.size > 0
              ? ` + ${pickedSecondaries.size} secondary`
              : ''}
          </button>
        )}
      </div>
    </div>
  );

  // ── State A: locked (read-only) ─────────────────────────────────
  if (locked && cards.length > 0) {
    const kept = cards.filter(c => c.selected !== false);
    return (
      <div data-testid="strategy-tab-panel" className="space-y-3">
        {categoryCard}
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-sm text-sm text-green-800">
          <span className="flex items-center gap-2">
            <Lock size={14} /> Strategy locked — {kept.length} direction{kept.length === 1 ? '' : 's'} applied to claim generation
          </span>
          <button
            type="button"
            data-testid="strategy-unlock-btn"
            onClick={doUnlock}
            disabled={unlocking}
            className="text-xs flex items-center gap-1 px-2 py-1 border border-zinc-300 bg-white rounded-sm hover:bg-zinc-50 disabled:opacity-50"
          >
            {unlocking ? <Loader2 size={11} className="animate-spin" /> : <Unlock size={11} />}
            Unlock to edit
          </button>
        </div>
        <ul className="space-y-2">
          {kept.map((c) => (
            <StrategyCardLocked
              key={c.id}
              matterId={matterId}
              card={c}
              onSaved={refreshMatter}
              onError={onError}
            />
          ))}
        </ul>
      </div>
    );
  }

  // ── State B: no cards yet ───────────────────────────────────────
  if (cards.length === 0) {
    return (
      <div data-testid="strategy-tab-panel" className="space-y-4">
        {categoryCard}
        <div className="border border-zinc-200 rounded-sm bg-white px-5 pt-3 pb-4">
          <h3 className="text-sm font-medium text-zinc-900 mb-1">Claim categories</h3>
          <p className="text-xs text-zinc-500 mb-2">
            Used as a hint when generating strategic direction cards.
          </p>
          <div className="flex flex-wrap gap-2" data-testid="strategy-claim-categories">
            {_CLAIM_CATEGORIES.map((c) => {
              const selected = claimCategories.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`strategy-cat-${c.id}`}
                  data-selected={selected}
                  onClick={() => toggleCategory(c.id)}
                  className={
                    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ' +
                    (selected
                      ? 'bg-[#002FA7] text-white border-[#002FA7]'
                      : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400')
                  }
                >
                  {selected && <Check size={11} />}
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
        {genError && (
          <div
            data-testid="strategy-gen-error"
            className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900 space-y-1"
          >
            <div className="flex items-center gap-1.5">
              <AlertTriangle size={12} /> {genError.reason}
            </div>
            {genError.raw && (
              <details>
                <summary className="cursor-pointer text-amber-800">raw response preview</summary>
                <pre className="mt-1 p-2 bg-white border border-amber-200 rounded-sm overflow-auto max-h-32 text-[10px]">
                  {genError.raw}
                </pre>
              </details>
            )}
          </div>
        )}
        <button
          type="button"
          data-testid="strategy-generate-cards-btn"
          onClick={doGenerate}
          disabled={generating}
          className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
          {generating ? 'Generating cards…' : 'Generate Direction Cards'}
        </button>
      </div>
    );
  }

  // ── State C: cards editable (default) ───────────────────────────
  return (
    <div data-testid="strategy-tab-panel" className="space-y-3">
      {categoryCard}
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-zinc-900">Strategic Directions</h3>
          <p className="text-xs text-zinc-500">
            Select the directions you want to apply. Add comments or refine any card.
          </p>
        </div>
        <button
          type="button"
          data-testid="strategy-regenerate-all-btn"
          onClick={doGenerate}
          disabled={generating}
          className="text-xs flex items-center gap-1 px-2 py-1 border border-zinc-300 bg-white rounded-sm hover:bg-zinc-50 disabled:opacity-50"
        >
          {generating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Regenerate all
        </button>
      </div>
      {genError && (
        <div
          data-testid="strategy-gen-error"
          className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900"
        >
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={12} /> {genError.reason}
          </div>
          <button
            type="button"
            data-testid="strategy-gen-retry"
            onClick={doGenerate}
            className="mt-1 text-amber-800 underline"
          >
            Retry
          </button>
        </div>
      )}
      <ul className="space-y-3">
        {cards.map((c) => {
          const open = discussOpenId === c.id;
          return (
            <li
              key={c.id}
              data-testid={`strategy-card-${c.id}`}
              className={
                'border rounded-sm bg-white px-4 py-3 transition-colors ' +
                (c.selected === false ? 'border-zinc-200 bg-zinc-50/50 opacity-60' : 'border-zinc-200')
              }
            >
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  data-testid={`strategy-card-select-${c.id}`}
                  checked={c.selected !== false}
                  onChange={(e) => patchCard(c.id, { selected: e.target.checked })}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <input
                      value={c.title}
                      onChange={(e) => patchCard(c.id, { title: e.target.value })}
                      data-testid={`strategy-card-title-${c.id}`}
                      className="text-sm font-medium text-zinc-900 bg-transparent border-0 border-b border-transparent hover:border-zinc-200 focus:border-[#002FA7] focus:outline-none w-full"
                    />
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest border shrink-0 ${priorityClass(c.priority)}`}>
                      {c.priority}
                    </span>
                    <button
                      type="button"
                      data-testid={`strategy-card-delete-${c.id}`}
                      onClick={() => removeCard(c.id)}
                      className="shrink-0 text-[11px] text-zinc-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                  <textarea
                    data-testid={`strategy-card-direction-${c.id}`}
                    value={c.direction}
                    onChange={(e) => patchCard(c.id, { direction: e.target.value })}
                    rows={3}
                    className="w-full bg-white border border-zinc-200 rounded-sm px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-transparent resize-y"
                  />
                  {c.rationale && (
                    <p className="text-xs text-zinc-500 mt-1 italic">Why: {c.rationale}</p>
                  )}
                  <input
                    data-testid={`strategy-card-comment-${c.id}`}
                    value={c.comment || ''}
                    onChange={(e) => patchCard(c.id, { comment: e.target.value })}
                    placeholder="Drafter note (optional)"
                    className="w-full bg-white border border-zinc-200 rounded-sm px-3 py-1.5 text-xs mt-2 focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-transparent"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`strategy-card-discuss-${c.id}`}
                      onClick={() => setDiscussOpenId(open ? null : c.id)}
                      className="text-[11px] text-[#002FA7] hover:text-[#002277] inline-flex items-center gap-1"
                    >
                      {open ? '× Close' : 'Discuss this →'}
                    </button>
                  </div>
                  {open && (
                    <ConversationPanel
                      matterId={matterId}
                      card={c}
                      onApplyRevision={(rev) => {
                        patchCard(c.id, { direction: rev });
                        setDiscussOpenId(null);
                      }}
                    />
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div>
        <button
          type="button"
          data-testid="strategy-add-custom-btn"
          onClick={addCustomCard}
          className="text-xs flex items-center gap-1 px-2 py-1 border border-dashed border-zinc-300 rounded-sm hover:bg-zinc-50"
        >
          + Add custom direction
        </button>
      </div>
      <div className="border-t border-zinc-200 pt-3 mt-3 space-y-3">
        <div className="flex items-center justify-between">
          <span data-testid="strategy-count" className="text-xs text-zinc-600">
            {selectedCount} of {cards.length} direction{cards.length === 1 ? '' : 's'} selected
          </span>
          {/* epi compliance Item 2 — Stage 2.5 review
              acknowledgment. AND-ed into the lock button below
              so drafters who enter the Strategy tab must tick
              before locking; the skip path (never enter 2.5)
              is unaffected because this surface isn't rendered
              at all on Stage 2's main tabs. Disabled once the
              strategy is already locked, matching the other
              stages' post-freeze behaviour. */}
          <StageAcknowledgment
            matterId={matterId}
            stage="stage_2_5"
            initialAcknowledgedAt={ackAt}
            onChange={setAckAt}
            disabled={locked}
            onError={onError}
          />
        </div>
        <div className="flex items-center justify-end">
          <button
            type="button"
            data-testid="strategy-lock-btn"
            onClick={doLock}
            disabled={locking || selectedCount === 0 || !ackAt}
            title={
              selectedCount === 0
                ? 'Select at least one direction'
                : !ackAt
                  ? 'Check the review-acknowledged box above to lock'
                  : 'Lock the strategy and feed into Stage 3'
            }
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {locking ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />}
            Lock Strategy →
          </button>
        </div>
      </div>

      {/* Low-selection confirmation modal — fires when <50% of
          cards are selected. The drafter sees how many are kept
          vs dropped + a warning that unselected cards do not
          flow into Stage 3. */}
      {pendingLowSelectionLock && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"
          data-testid="lock-low-selection-modal"
          onClick={() => setPendingLowSelectionLock(false)}
        >
          <div
            className="bg-white rounded-md shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-zinc-900">
                  Low-selection lock
                </div>
                <div className="text-sm text-zinc-700 mt-1 leading-snug">
                  You have selected{' '}
                  <span className="font-semibold">{selectedCount}</span> of{' '}
                  <span className="font-semibold">{cards.length}</span>{' '}
                  strategy cards. The unselected cards will not be
                  considered during claim drafting. Proceed?
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                data-testid="lock-low-selection-cancel"
                onClick={() => setPendingLowSelectionLock(false)}
                className="text-sm px-3 py-1.5 text-zinc-700 bg-white border border-zinc-300 rounded-sm hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="lock-low-selection-confirm"
                onClick={async () => {
                  setPendingLowSelectionLock(false);
                  await performLock();
                }}
                className="text-sm px-3 py-1.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#002277]"
              >
                Lock anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * StrategyCardLocked — single card row with per-card unfreeze flow.
 *
 * Three states match StatementCard's mode model:
 *   frozen      — read-only display, [Unfreeze] button
 *   manual_edit — title + direction become editable, amber notice,
 *                 [Save & Re-freeze] + [Cancel] buttons
 *
 * (There's no third 'editable' state on the locked panel — cards
 * either come from the bulk lock with frozen=true, or get
 * unfrozen one at a time. The editable / pre-lock variant lives
 * in StrategyCardsTabPanel's State C above.)
 *
 * Cascade rule (matches the field- and claim-level pattern):
 *   - Save & Re-freeze writes the new card body to
 *     strategy_cards_json. The stage-level claim_strategy_frozen,
 *     the cards summary text in matter_indexes, and Stage 3 are
 *     ALL untouched. Drafter takes responsibility for the
 *     resulting drift; the amber notice makes that explicit.
 */
function StrategyCardLocked({
  matterId, card, onSaved, onError,
}: {
  matterId: string;
  card: api.StrategyCard;
  onSaved: () => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const [editing, setEditing] = React.useState<boolean>(card.frozen === false);
  const [draftTitle, setDraftTitle] = React.useState<string>(card.title);
  const [draftDirection, setDraftDirection] = React.useState<string>(card.direction);
  const [draftComment, setDraftComment] = React.useState<string>(card.comment || '');
  const [busy, setBusy] = React.useState(false);
  // Snapshot of the original values so Cancel can revert without
  // a server round-trip. Captured the moment the user clicks
  // Unfreeze; refreshed if the parent re-renders with new card
  // props (e.g. after a successful save → new "original").
  const [original, setOriginal] = React.useState({
    title: card.title, direction: card.direction, comment: card.comment || '',
  });

  React.useEffect(() => {
    // Sync local drafts when the parent reloads card props after a
    // save. Don't clobber in-flight edits — only sync when not
    // currently editing.
    if (!editing) {
      setDraftTitle(card.title);
      setDraftDirection(card.direction);
      setDraftComment(card.comment || '');
      setOriginal({ title: card.title, direction: card.direction, comment: card.comment || '' });
    }
  }, [card.title, card.direction, card.comment, editing]);

  const beginEdit = async () => {
    setBusy(true);
    onError(null);
    try {
      await api.unfreezeStrategyCard(matterId, card.id);
      setOriginal({ title: card.title, direction: card.direction, comment: card.comment || '' });
      setEditing(true);
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Could not unfreeze card.');
    } finally {
      setBusy(false);
    }
  };

  const saveAndRefreeze = async () => {
    if (!draftTitle.trim() || !draftDirection.trim()) {
      onError('Title and direction are required.');
      return;
    }
    setBusy(true);
    onError(null);
    try {
      await api.patchStrategyCard(matterId, card.id, {
        title: draftTitle.trim(),
        direction: draftDirection.trim(),
        comment: draftComment.trim(),
      });
      await api.freezeStrategyCard(matterId, card.id);
      setEditing(false);
      await onSaved();
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Save & Re-freeze failed.');
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = async () => {
    setBusy(true);
    onError(null);
    try {
      // Revert local state immediately for snappy UX.
      setDraftTitle(original.title);
      setDraftDirection(original.direction);
      setDraftComment(original.comment);
      // Re-freeze the card on the server using the current frozen
      // body — patch endpoint refuses while frozen, so push the
      // original body first to make sure server-side reflects the
      // un-edited state.
      try {
        await api.patchStrategyCard(matterId, card.id, {
          title: original.title,
          direction: original.direction,
          comment: original.comment,
        });
      } catch { /* tolerate — re-freeze still goes through */ }
      await api.freezeStrategyCard(matterId, card.id);
      setEditing(false);
      await onSaved();
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Cancel failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      data-testid={`strategy-locked-card-${card.id}`}
      data-mode={editing ? 'manual_edit' : 'frozen'}
      className="border border-zinc-200 rounded-sm bg-white px-4 py-3"
    >
      <div className="flex items-center gap-2 mb-1">
        {editing ? (
          <input
            type="text"
            data-testid={`strategy-locked-card-title-edit-${card.id}`}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            disabled={busy}
            className="flex-1 text-sm font-medium text-zinc-900 bg-white border border-zinc-300 rounded-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
          />
        ) : (
          <span className="text-sm font-medium text-zinc-900">{card.title}</span>
        )}
        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest border ${priorityClass(card.priority)}`}>
          {card.priority}
        </span>
        {!editing && (
          <button
            type="button"
            data-testid={`strategy-card-unfreeze-${card.id}`}
            onClick={beginEdit}
            disabled={busy}
            title="Unfreeze for manual editing — Stage 3 prompt context will not auto-update"
            className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 disabled:opacity-50"
          >
            <Unlock size={11} /> Unfreeze
          </button>
        )}
        {editing && (
          <>
            <button
              type="button"
              data-testid={`strategy-card-save-refreeze-${card.id}`}
              onClick={saveAndRefreeze}
              disabled={busy || !draftTitle.trim() || !draftDirection.trim()}
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Lock size={11} />}
              Save &amp; Re-freeze
            </button>
            <button
              type="button"
              data-testid={`strategy-card-cancel-edit-${card.id}`}
              onClick={cancelEdit}
              disabled={busy}
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {editing && (
        <div
          data-testid={`strategy-card-manual-edit-notice-${card.id}`}
          className="mt-2 mb-2 p-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900 flex items-start gap-1.5"
        >
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-600" />
          <span>
            Manual edit. Stage 3 prompt context and matter_indexes
            keep the previous text until you re-run claim generation.
          </span>
        </div>
      )}

      {editing ? (
        <textarea
          data-testid={`strategy-locked-card-direction-edit-${card.id}`}
          value={draftDirection}
          onChange={(e) => setDraftDirection(e.target.value)}
          disabled={busy}
          rows={3}
          className="w-full text-sm text-zinc-700 leading-relaxed bg-white border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-y"
        />
      ) : (
        <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">
          {card.direction}
        </p>
      )}

      {editing ? (
        <input
          type="text"
          data-testid={`strategy-locked-card-comment-edit-${card.id}`}
          value={draftComment}
          onChange={(e) => setDraftComment(e.target.value)}
          disabled={busy}
          placeholder="Drafter note (optional)"
          className="mt-2 w-full text-xs text-zinc-600 italic bg-white border border-zinc-300 rounded-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
        />
      ) : card.comment ? (
        <p className="text-xs text-zinc-600 italic mt-2">Drafter note: {card.comment}</p>
      ) : null}
    </li>
  );
}


function ConversationPanel({
  matterId, card, onApplyRevision,
}: {
  matterId: string;
  card: api.StrategyCard;
  onApplyRevision: (rev: string) => void;
}) {
  const [history, setHistory] = React.useState<Array<{ role: 'drafter' | 'advisor'; text: string }>>([]);
  const [draft, setDraft] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [revision, setRevision] = React.useState<string | null>(null);

  const suggestedPrompts = [
    'Make this broader',
    'Make this more conservative',
    'Why is this recommended?',
    'What are the risks?',
    'Suggest alternative wording',
  ];

  const send = async (message: string) => {
    const msg = message.trim();
    if (!msg) return;
    setSending(true);
    setDraft('');
    const newHistory: Array<{ role: 'drafter' | 'advisor'; text: string }> = [
      ...history, { role: 'drafter', text: msg },
    ];
    setHistory(newHistory);
    try {
      const resp = await api.strategyCardConversation(matterId, {
        card_id: card.id,
        card_content: `${card.title}\n\n${card.direction}`,
        message: msg,
        history,
      });
      setHistory([...newHistory, { role: 'advisor', text: resp.response }]);
      if (resp.revised_direction) setRevision(resp.revised_direction);
      else setRevision(null);
    } catch {
      setHistory([
        ...newHistory,
        { role: 'advisor', text: 'Sorry, the advisor couldn’t respond. Try again.' },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid={`strategy-discuss-panel-${card.id}`}
      className="mt-3 border border-zinc-200 rounded-sm bg-zinc-50/60 p-3 space-y-2"
    >
      <div className="flex flex-wrap gap-1">
        {suggestedPrompts.map((p) => (
          <button
            key={p}
            type="button"
            data-testid={`strategy-discuss-chip-${card.id}-${p.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => send(p)}
            disabled={sending}
            className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 disabled:opacity-50"
          >
            {p}
          </button>
        ))}
      </div>
      {history.length > 0 && (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {history.map((h, i) => (
            <div
              key={i}
              className={
                'text-xs rounded-sm px-2 py-1 ' +
                (h.role === 'drafter'
                  ? 'bg-blue-50 text-[#002FA7] ml-6 text-right'
                  : 'bg-white border border-zinc-200 text-zinc-800 mr-6')
              }
            >
              {h.text}
            </div>
          ))}
        </div>
      )}
      {revision && (
        <div
          data-testid={`strategy-discuss-revision-${card.id}`}
          className="border border-green-200 bg-green-50 rounded-sm px-3 py-2 text-xs"
        >
          <div className="text-green-800 font-medium mb-1">Suggested revision:</div>
          <p className="text-zinc-800 whitespace-pre-wrap">{revision}</p>
          <button
            type="button"
            data-testid={`strategy-discuss-apply-${card.id}`}
            onClick={() => onApplyRevision(revision)}
            className="mt-2 text-xs font-medium text-green-800 hover:text-green-900 underline"
          >
            Apply revision
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(draft); } }}
          placeholder="Type a question or request…"
          data-testid={`strategy-discuss-input-${card.id}`}
          disabled={sending}
          className="flex-1 bg-white border border-zinc-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:bg-zinc-50"
        />
        <button
          type="button"
          data-testid={`strategy-discuss-send-${card.id}`}
          onClick={() => send(draft)}
          disabled={sending || !draft.trim()}
          className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1 text-xs font-medium disabled:opacity-50"
        >
          {sending ? <Loader2 size={11} className="animate-spin" /> : 'Ask'}
        </button>
      </div>
    </div>
  );
}

